/* eslint-disable @typescript-eslint/no-unused-vars */
import { Checkbox, Collapse, Tree } from 'antd';
import { useEffect, useState } from 'react';
import { getAdminSupportServicePermissionsList } from '../../../../../api';
import getFormatMessage from '../../../../../locales/get-format-message';
import { IServicePermissions, RbacRoleType } from '../../../../../model';
import { RbacAdminPermObjectKey } from '../../../../../model/api-modules';
import accessOneTrue from '../../../../../assets/svg/access-one-true.svg';
import accessOneFalse from '../../../../../assets/svg/access-one-false.svg';
import accessTwoTrue from '../../../../../assets/svg/access-two-true.svg';
import accessTwoFalse from '../../../../../assets/svg/access-two-false.svg';
import styles from './index.module.scss';
import { groupBy, omit } from 'lodash';

const { Panel } = Collapse;

interface IProps {
  roleType: RbacRoleType;
  viewOnly?: boolean;
  onChange?: (values?: RbacAdminPermObjectKey[]) => void;
  value?: RbacAdminPermObjectKey[];
}

interface IPermTreeItem {
  label: string;
  children?: IPermTreeChild[];
  value: RbacAdminPermObjectKey[];
}

interface IPermTreeChild {
  label: string;
  children?: IPermTreeChild[];
  value?: RbacAdminPermObjectKey;
}

export const SupportAccessRights = (props: IProps) => {
  const { value, onChange, viewOnly = false, roleType } = props;
  const [permList, setPermList] = useState<IServicePermissions>();
  const [permTree, setPermTree] = useState<readonly IPermTreeItem[]>([]);

  // 不可编辑的权限类别
  const disabledRightSet = ['User Management', 'Billing Management'];

  // 不可编辑的权限
  const disabledPerms: string[] = [RbacAdminPermObjectKey.GameManageEdit];

  // 权限级联配置
  const permTypeConfigs: Record<string, RbacAdminPermObjectKey[]> = {
    游戏管理: [
      RbacAdminPermObjectKey.GameManageEdit,
      RbacAdminPermObjectKey.GameManageView,
    ],
  };

  const permTypeMap = Object.keys(permTypeConfigs)
    .map((type) => {
      return permTypeConfigs[type].reduce<
        Partial<Record<RbacAdminPermObjectKey, string>>
      >((acc, permKey) => {
        acc[permKey] = type;
        return acc;
      }, {});
    })
    .reduce((acc, item) => {
      return { ...acc, ...item };
    }, {});

  const orgTopMenu = 'Support Access';

  const rightLabelMap: Record<string, string> = {
    'User Management': getFormatMessage('user_manage'),
    'Studio Management': getFormatMessage('studio_management'),
    'Billing Management': getFormatMessage('billing_manage'),
    'Platform Operation': getFormatMessage('platform_operation'),
    'Customer Contact': getFormatMessage('customer_contact'),
    [RbacAdminPermObjectKey.UserManage]: getFormatMessage('user_list'),
    [RbacAdminPermObjectKey.AdminAccount]: getFormatMessage(
      'business_account_approve'
    ),
    [RbacAdminPermObjectKey.GameManageView]: getFormatMessage('game_perm_view'),
    [RbacAdminPermObjectKey.ServiceFeeManage]:
      getFormatMessage('price_calculator'),
    [RbacAdminPermObjectKey.CompanyCertification]:
      getFormatMessage('menu_company_auth'),
    [RbacAdminPermObjectKey.FeeCenter]: getFormatMessage('withdrawal_list'),
    [RbacAdminPermObjectKey.ServiceManage]: getFormatMessage('service_online'),
    [RbacAdminPermObjectKey.DownloadManage]:
      getFormatMessage('download_manage'),
    [RbacAdminPermObjectKey.DownloadRecord]:
      getFormatMessage('download_record'),
    [RbacAdminPermObjectKey.ServiceApply]: getFormatMessage(
      'service_application'
    ),
    [RbacAdminPermObjectKey.UpgradeNotification]: getFormatMessage(
      'upgrade_notification'
    ),
    [RbacAdminPermObjectKey.NewsManage]: getFormatMessage('download_news'),
    [RbacAdminPermObjectKey.ContactUS]: getFormatMessage('mail_record'),
    [RbacAdminPermObjectKey.MessageManage]: getFormatMessage('message_push'),
    [RbacAdminPermObjectKey.BusinessNotice]:
      getFormatMessage('business_notice'),
    [RbacAdminPermObjectKey.ApplySupportAccount]:
      getFormatMessage('业务账号申请'),
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    getTree();
  }, [value, permList]);

  const getTree = () => {
    if (!permList) {
      return;
    }
    const tree = permList.objectGroup.map((group) => {
      const objects = permList.objects.filter(
        (item) => item.objectGroupId === group.objectGroupId
      );

      const getChildren = () => {
        const groupedRights = groupBy(
          objects
            .map((item) => ({
              label: rightLabelMap[item.objectKey] || item.objectName || '',
              value: item.objectKey as RbacAdminPermObjectKey,
              type:
                permTypeMap[item.objectKey as RbacAdminPermObjectKey] ||
                'empty',
            }))
            .sort((a, b) => {
              const permKeys = Object.values(RbacAdminPermObjectKey);
              const aIndex = permKeys.indexOf(a.value);
              const bIndex = permKeys.indexOf(b.value);
              if (aIndex === -1) {
                return 1;
              }
              return aIndex > bIndex ? 1 : -1;
            }),
          'type'
        );

        const children: IPermTreeChild[] = [
          ...(groupedRights.empty || []),
          ...Object.keys(groupedRights)
            .filter((item) => item !== 'empty')
            .map((type) => {
              return {
                label: type,
                children: groupedRights[type],
              };
            })
            .filter(Boolean),
        ];
        return children;
      };

      const children = getChildren();
      const getValue = () => {
        const childrenRights = children
          .map(
            (item) =>
              item.value || item.children?.map((item) => item.value) || []
          )
          .filter(Boolean)
          .flat();
        const selectedRights = value || [];
        return selectedRights.filter((right) => childrenRights.includes(right));
      };

      const treeItem = {
        label: group.objectGroupName,
        children,
        value: getValue(),
      };

      return treeItem;
    });

    setPermTree(tree);
  };

  const init = async () => {
    const perms = await getAdminSupportServicePermissionsList();
    if (!perms) {
      return;
    }
    setPermList(perms);
  };

  const getExtra = (group: IPermTreeItem) => {
    if (
      roleType === RbacRoleType.Custom &&
      disabledRightSet.includes(group.label)
    ) {
      return getFormatMessage('role_has_no_rights');
    }
    return null;
  };

  const onRightsChange = (
    changedValues: RbacAdminPermObjectKey[] = [],
    group: IPermTreeItem
  ) => {
    const newRights = permTree.reduce<RbacAdminPermObjectKey[]>(
      (acc, curGroup) => {
        if (curGroup.label === group.label) {
          return [...acc, ...changedValues];
        }
        return [...acc, ...(curGroup.value || [])] as RbacAdminPermObjectKey[];
      },
      []
    );

    if (newRights.length) {
      onChange?.(newRights);
      return;
    }
    onChange?.(undefined);
  };

  const filterTree = permTree.filter((item) => item.label !== orgTopMenu);

  const renderViewContent = () => {
    if (!viewOnly) {
      return null;
    }
    return (
      <Collapse expandIconPosition="right">
        {filterTree.map((group) => {
          const noPerms = !group.value.length;
          const getViewOnlyHeader = () => {
            const icon = noPerms ? accessOneFalse : accessOneTrue;
            return (
              <span className={styles.viewPanelHeader}>
                <img src={icon} />
                <span>{rightLabelMap[group.label] || ''}</span>
              </span>
            );
          };

          return (
            <Panel
              header={getViewOnlyHeader()}
              key={group.label}
              extra={getExtra(group)}
              collapsible={noPerms ? 'disabled' : undefined}
            >
              <div className={styles.viewPermsPanel}>
                {group.children?.map((groupPermInfo) => {
                  if (!groupPermInfo.children && groupPermInfo.value) {
                    const isSelected = group.value.includes(
                      groupPermInfo.value
                    );
                    const icon = isSelected ? accessTwoTrue : accessTwoFalse;
                    return (
                      <div className={styles.viewPermItem}>
                        <span>
                          <img src={icon} />
                        </span>
                        <span>
                          {rightLabelMap[groupPermInfo.value] ||
                            groupPermInfo.label ||
                            ''}
                        </span>
                      </div>
                    );
                  }

                  if (groupPermInfo.children) {
                    return (
                      <div>
                        <div>{groupPermInfo.label}</div>
                        <div>
                          {groupPermInfo.children.map((permInfo) => {
                            const isSelected =
                              permInfo.value &&
                              group.value.includes(permInfo.value);
                            const icon = isSelected
                              ? accessTwoTrue
                              : accessTwoFalse;
                            return (
                              <div>
                                <span>
                                  <img src={icon} />
                                </span>
                                <span>
                                  {rightLabelMap[permInfo.value || ''] ||
                                    permInfo.label ||
                                    ''}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  return null;
                })}
              </div>
            </Panel>
          );
        })}
      </Collapse>
    );
  };

  const getGroupAllRights = (group: IPermTreeItem) => {
    if (!group.children) {
      return [];
    }
    return group.children
      .map(
        (item) => item.value || item.children?.map((child) => child.value) || []
      )
      .flat()
      .filter(Boolean) as RbacAdminPermObjectKey[];
  };

  const renderEditContent = () => {
    if (viewOnly) {
      return null;
    }
    return (
      <Collapse expandIconPosition="right">
        {filterTree.map((group) => {
          const isDisabled = disabledRightSet.includes(group.label);

          const getHeader = () => {
            const allRights = getGroupAllRights(group);
            return (
              <>
                <Checkbox
                  disabled={isDisabled}
                  onClick={(ev) => ev.stopPropagation()}
                  onChange={(ev) => {
                    const { checked } = ev.target;
                    if (checked) {
                      onRightsChange(allRights, group);
                      return;
                    }
                    onRightsChange([], group);
                  }}
                  indeterminate={
                    group.value.length > 0 &&
                    group.value.length < allRights.length
                  }
                  checked={group.value.length === allRights.length}
                />
                {rightLabelMap[group.label] || ''}
              </>
            );
          };

          const renderRights = () => {
            if (!group.children) {
              return null;
            }
            return group.children.map((child) => {
              if (!child.children && child.value) {
                return (
                  <Checkbox
                    onChange={(ev) => {
                      const { checked } = ev.target;
                      onRightsChange(
                        (checked
                          ? [...group.value, child.value]
                          : group.value.filter(
                              (item) => item !== child.value
                            )) as RbacAdminPermObjectKey[],
                        group
                      );
                    }}
                    checked={group.value.includes(child.value)}
                    value={child.value}
                  >
                    {child.label}
                  </Checkbox>
                );
              }

              if (child.children) {
                const treeData = [
                  {
                    title: child.label,
                    key: child.label,
                    children: child.children.map((item) => {
                      return {
                        title: item.label,
                        key: item.value as string,
                        disabled: disabledPerms.includes(item.value || ''),
                      };
                    }),
                  },
                ];
                const treeCheckedKeys = treeData
                  .map((item) => item.children)
                  .flat()
                  .map((item) => item.key)
                  .filter((key) =>
                    group.value.includes(key as RbacAdminPermObjectKey)
                  );
                return (
                  <Tree
                    checkable
                    treeData={treeData}
                    checkedKeys={treeCheckedKeys}
                    onCheck={(keys: any) => {
                      const permKeys = keys.filter(
                        (key: RbacAdminPermObjectKey) =>
                          Object.values(RbacAdminPermObjectKey).includes(key)
                      );

                      const newPerms = [
                        ...group.value.filter(
                          (perm) =>
                            !child.children
                              ?.map((sub) => sub.value)
                              .includes(perm)
                        ),
                        ...permKeys,
                      ] as RbacAdminPermObjectKey[];
                      onRightsChange(newPerms, group);
                    }}
                  />
                );
              }
              return null;
            });
          };
          return (
            <Panel
              header={getHeader()}
              key={group.label}
              extra={getExtra(group)}
              collapsible={isDisabled ? 'disabled' : undefined}
            >
              <div className={styles.editPermGroup}>{renderRights()}</div>
            </Panel>
          );
        })}
      </Collapse>
    );
  };

  return (
    <div>
      {renderEditContent()}
      {renderViewContent()}
    </div>
  );
};
